/**
 * Data Import React Query Hooks
 *
 * Custom hooks for data import operations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dataImportApi, TaskImportData, CompetencyImportData } from '../../lib/dataImportApi';

// Query keys
export const dataImportKeys = {
  all: ['dataImport'] as const,
  summary: () => [...dataImportKeys.all, 'summary'] as const,
};

/**
 * Hook to fetch data summary
 */
export function useDataSummary() {
  return useQuery({
    queryKey: dataImportKeys.summary(),
    queryFn: () => dataImportApi.getSummary(),
  });
}

/**
 * Hook to seed niveaus
 */
export function useSeedNiveaus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => dataImportApi.seedNiveaus(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dataImportKeys.summary() });
      queryClient.invalidateQueries({ queryKey: ['niveaus'] });
    },
  });
}

/**
 * Hook to seed all default data
 */
export function useSeedAll() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => dataImportApi.seedAll(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dataImportKeys.summary() });
      queryClient.invalidateQueries({ queryKey: ['niveaus'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['competencies'] });
    },
  });
}

/**
 * Hook to import tasks
 */
export function useImportTasks() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tasks: TaskImportData[]) => dataImportApi.importTasks(tasks),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dataImportKeys.summary() });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

/**
 * Hook to import competencies
 */
export function useImportCompetencies() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (competencies: CompetencyImportData[]) => dataImportApi.importCompetencies(competencies),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dataImportKeys.summary() });
      queryClient.invalidateQueries({ queryKey: ['competencies'] });
    },
  });
}

/**
 * Hook to sync employees from MySQL
 */
export function useSyncEmployees() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => dataImportApi.syncEmployees(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dataImportKeys.summary() });
      queryClient.invalidateQueries({ queryKey: ['employees'] });
    },
  });
}
